import { Table as BaseTable, TableProps } from '@/components/table'
import { Query } from './table_query'
import { AssetsQuery } from './__generated__/table_query'
import { useQuery } from '@apollo/client'
import { Inline as Error } from '@/components/error'
import { Inline as Loading } from '@/components/loading'
import { AssetFilters, AssetOrder, AssetTypeFilters, SortOrder } from '@/types'
import { useTable } from '@/helpers/useTable'
import { Button } from '../base/button'
import { dedupe } from '@/helpers/dedupe'
import { ShowDialog } from './show_dialog'
import { NewDialog } from './new_dialog'

interface Props extends TableProps {
  filters?: AssetFilters
  createable?: boolean
}

export const Table = ({ pageSize = 10, filters, onIndex = false, createable = false, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery<AssetsQuery>(
    Query,
    { variables: { filters: filters || {}, first: pageSize } }
  )
  const { pageInfo, openNew, setOpenNew, openShow, showShow, hideShow, resourceId } = useTable({ pageInfo: data?.assets?.pageInfo, onIndex, basePath: '/assets' })

  if (error) { return <Error message={error.message} /> }
  if (loading || !data) { return <Loading /> }

  return (
    <>
      <BaseTable<AssetTypeFilters, AssetOrder>
        {...props}
        actionButton={createable ? <Button color="green" onClick={() => setOpenNew(true)}>Add</Button> : undefined}
        defaultSort={{ barcode: SortOrder.Asc }}
        initialFilters={filters}
        moreToFetch={pageInfo.current?.hasNextPage}
        refetch={refetch}
        fetchMore={() => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.assets.nodes = dedupe([
              ...previousData.assets.nodes,
              ...fetchMoreResult.assets.nodes
            ])
            return { ...fetchMoreResult }
          }
        })}
        filters={[
          {
            index: 2,
            setValue: (set, value) => set(f => ({ ...f, serialNumberEq: value })),
            type: 'text',
            placeholder: 'What does the tag say?'
          },
        ]}
        headings={[
          { name: 'Name' },
          { name: 'Barcode', sortField: 'barcode', defaultDirection: SortOrder.Asc },
          { name: 'Serial Number' },
          { name: 'Warehouse' }
        ]}
        rows={data.assets.nodes.map(asset => (
          {
            id: asset.id,
            columns: [
              asset.name,
              asset.barcode.barcode,
              asset.serialNumber,
              asset.warehouse?.name

            ]
          }
        ))}
        onRowClick={showShow}
      />
      { resourceId && <ShowDialog id={resourceId} open={openShow} setOpen={hideShow} /> }
      <NewDialog open={openNew} setOpen={setOpenNew} />
    </>
  )
}
